import {useCallback, useEffect, useState} from "react";
import {Code, FileSpreadsheet, Save} from "lucide-react";
import {
  adminSeoExportUrl,
  createAdminSeoEntry,
  deleteAdminSeoEntry,
  getAdminGoogleAnalytics,
  listAdminSeoEntries,
  listAdminSeoPageFiles,
  updateAdminGoogleAnalytics,
  updateAdminSeoEntry,
  type AdminSeoEntry
} from "@corporatesclub/shared";
import {useAdminConfirm} from "@/hooks/useAdminConfirm";
import {adminSearchText} from "@/lib/adminSearchText";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Textarea} from "@/components/ui/textarea";
import {AdminAddButton, AdminAlert, AdminBadge, AdminCard, AdminEditModal, AdminField, AdminFormActions, AdminModernTable, AdminPageLayout, AdminPagination, AdminToolbar} from "./ui";

const emptyForm = {pagina: "", titlu: "", cuvinteCheie: "", descriere: ""};

export function SeoView() {
  const {confirm, dialog: confirmDialog} = useAdminConfirm();
  const [items, setItems] = useState<AdminSeoEntry[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pageFiles, setPageFiles] = useState<string[]>([]);
  const [gaCode, setGaCode] = useState("");
  const [showGa, setShowGa] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openId, setOpenId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    listAdminSeoEntries({page, pageSize: 20, search: searchQuery || undefined}).then((r) => {
      setItems(r.items);
      setTotalPages(r.totalPages);
    }).catch((e: Error) => setErr(e.message));
  }, [page, searchQuery]);
  useEffect(() => {
    load();
    listAdminSeoPageFiles().then((r) => setPageFiles(r.items)).catch(() => {});
    getAdminGoogleAnalytics().then((r) => setGaCode(r.code)).catch(() => {});
  }, [load]);

  const commitSearch = useCallback(() => {
    setSearchQuery(search);
    setPage(1);
  }, [search]);

  function closeModal(open: boolean) {
    setModalOpen(open);
    if(!open) setOpenId(null);
  }
  function openEntry(e: AdminSeoEntry) {
    setOpenId(e.id);
    setForm({pagina: e.pagina, titlu: e.titlu, cuvinteCheie: e.cuvinteCheie, descriere: e.descriere});
    setModalOpen(true);
  }
  function openNew() {
    setOpenId("new");
    setForm(emptyForm);
    setModalOpen(true);
  }
  async function saveEntry() {
    try {
        if(openId === "new") {
          await createAdminSeoEntry(form);
          adminToast.success("SEO entry added.");
        } else if(typeof openId === "number") {
          await updateAdminSeoEntry(openId, form);
          adminToast.success("SEO entry saved.");
        }
      closeModal(false);
      load();
    } catch(e) {
      adminToast.error(e instanceof Error ? e.message : "Save failed");
    }
  }
  function removeEntry(id: number) {
    confirm("Delete?", async () => {
      await deleteAdminSeoEntry(id);
      closeModal(false);
      load();
      adminToast.success("Entry deleted.");
    });
  }
  if(err) return <AdminAlert variant="error">{err}</AdminAlert>;
  return (
    <AdminPageLayout
      title="SEO"
      description="Meta tags and Google Analytics"
      breadcrumbs={[{label: "Dashboard", href: "/vadmin"}, {label: "SEO"}]}
      actions={<AdminAddButton onClick={openNew} label="Add SEO entry" />}
    >
      {confirmDialog}
      <AdminToolbar>
        <Button type="button" variant={showGa ? "default" : "secondary"} size="sm" onClick={() => setShowGa(!showGa)}>
          <Code className="h-4 w-4" /> Google Analytics
        </Button>
        <Button variant="secondary" size="sm" asChild>
          <a href={adminSeoExportUrl()} target="_blank" rel="noreferrer"><FileSpreadsheet className="h-4 w-4" /> Export meta tags</a>
        </Button>
      </AdminToolbar>
      {showGa ? (
        <AdminCard title="Google Analytics">
          <AdminField label="Google Analytics code">
            <Textarea value={gaCode} onChange={(e) => setGaCode(e.target.value)} rows={4} placeholder="Google Analytics code" />
          </AdminField>
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => updateAdminGoogleAnalytics(gaCode).then(() => adminToast.success("Saved."))}>
              <Save className="h-4 w-4" /> Save GA
            </Button>
          </AdminFormActions>
        </AdminCard>
      ) : null}
      <AdminModernTable
        items={items}
        getId={(e) => e.id}
        getTitle={(e) => e.titlu}
        getSubtitle={(e) => e.pagina}
        getMeta={(e) => <AdminBadge variant="outline">{e.pagina}</AdminBadge>}
        getSearchText={(e) => adminSearchText(e.id, e.pagina, e.titlu, e.cuvinteCheie, e.descriere)}
        searchValue={search}
        onSearchChange={setSearch}
        onSearchCommit={commitSearch}
        clientFilter={false}
        selectedId={typeof openId === "number" ? openId : null}
        onRowClick={openEntry}
        emptyTitle="No SEO entries"
        emptyDescription="Add a page or adjust search."
        searchPlaceholder="Search page, title, keywords, description..."
      />
      <AdminEditModal
        open={modalOpen}
        onOpenChange={closeModal}
        title={openId === "new" ? "New SEO entry" : "Edit meta tags"}
        icon={Code}
        footer={(
          <AdminFormActions>
            <Button type="button" size="sm" onClick={() => saveEntry()}>Save</Button>
            {typeof openId === "number" ? (
              <Button type="button" variant="outline" size="sm" onClick={() => removeEntry(openId)}>Delete</Button>
            ) : null}
          </AdminFormActions>
        )}
      >
        <AdminField label="Page">
          <Select value={form.pagina || undefined} onValueChange={(v) => setForm({...form, pagina: v})}>
            <SelectTrigger><SelectValue placeholder="Page" /></SelectTrigger>
            <SelectContent>
              {pageFiles.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </AdminField>
        <AdminField label="Title">
          <Input value={form.titlu} onChange={(ev) => setForm({...form, titlu: ev.target.value})} placeholder="Title" />
        </AdminField>
        <AdminField label="Keywords">
          <Input value={form.cuvinteCheie} onChange={(ev) => setForm({...form, cuvinteCheie: ev.target.value})} placeholder="Keywords" />
        </AdminField>
        <AdminField label="Description">
          <Textarea value={form.descriere} onChange={(ev) => setForm({...form, descriere: ev.target.value})} rows={3} />
        </AdminField>
      </AdminEditModal>
      <AdminPagination page={page} totalPages={totalPages} onPrev={() => setPage(page - 1)} onNext={() => setPage(page + 1)} />
    </AdminPageLayout>
  );
}
